/**
 * Wrapper resiliente em volta de localStorage/sessionStorage.
 *
 * Por que existe: `localStorage.setItem` pode lançar exception em vários
 * cenários que NÃO devem quebrar o app:
 *
 *  - QuotaExceededError: storage cheio (iPhone com armazenamento no limite,
 *    PWA em modo standalone com quota baixa)
 *  - SecurityError: modo privado do Safari ou ITP-restricted
 *  - DOMException genérica em iOS quando o usuário desabilita "JavaScript"
 *    via Configurações > Safari > Avançado
 *  - localStorage === null em alguns webviews
 *
 * Esses wrappers nunca lançam. Operações falhas são silenciosamente
 * descartadas (com um aviso opcional no console em dev). O caller pode
 * verificar o boolean de retorno se quiser saber se a escrita persistiu.
 *
 * **Importante**: usar sempre esses helpers em vez de localStorage direto.
 * O código antigo continua funcionando — só não fica robusto à falha.
 */

type StorageType = 'local' | 'session';

function getStorage(type: StorageType): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Lê uma chave. Retorna `null` se não existe, ou se storage tá indisponível.
 * NUNCA lança.
 */
export function safeGet(key: string, type: StorageType = 'local'): string | null {
  const s = getStorage(type);
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Escreve uma chave. Retorna `true` se foi persistido, `false` se falhou
 * (storage cheio, modo privado, etc). O caller pode decidir fallback,
 * ex.: manter em memória dentro de um Set, mostrar toast pro user, etc.
 */
export function safeSet(key: string, value: string, type: StorageType = 'local'): boolean {
  const s = getStorage(type);
  if (!s) return false;
  try {
    s.setItem(key, value);
    return true;
  } catch (err) {
    // Em dev, logar pra facilitar debug. Em prod, silencioso.
    if (import.meta.env.DEV) {
      console.warn(`[safeStorage] setItem("${key}") falhou:`, err);
    }
    return false;
  }
}

/** Remove uma chave. Sempre retorna void. NUNCA lança. */
export function safeRemove(key: string, type: StorageType = 'local'): void {
  const s = getStorage(type);
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    /* silenciosamente ignora */
  }
}

/**
 * Helper pra JSON com fallback. Retorna `fallback` se a chave não existe
 * OU se o JSON é inválido (corrupção em iOS é mais comum do que parece).
 */
export function safeGetJSON<T>(key: string, fallback: T, type: StorageType = 'local'): T {
  const raw = safeGet(key, type);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Helper pra escrever JSON. Retorna boolean indicando sucesso.
 * Se o valor não for serializável (ex.: contém função, BigInt), retorna false.
 */
export function safeSetJSON(key: string, value: unknown, type: StorageType = 'local'): boolean {
  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return false;
  }
  return safeSet(key, serialized, type);
}

/**
 * Testa se localStorage tá realmente disponível e gravável. Útil pra
 * decidir features (ex.: "lembrar preferências" só ativa se passa).
 */
export function isStorageAvailable(type: StorageType = 'local'): boolean {
  const s = getStorage(type);
  if (!s) return false;
  const probe = '__sixpass_probe__';
  try {
    s.setItem(probe, '1');
    s.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}
